/**
 * product-form.js — <product-form>
 *
 * Intercepts the add-to-cart submit so the visitor stays on the page. Without
 * this file the form posts normally and Shopify handles it — the page just
 * reloads into the cart.
 *
 * Errors are shown, never swallowed. An add that fails because the last unit
 * sold thirty seconds ago has to say so, or the storefront has lied.
 */

import { announce } from './theme.js';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.button = this.querySelector('[data-add-to-cart]');
    this.errorTarget = this.querySelector('[data-product-form-error]');
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-quantity-step]');
      if (!step) return;
      event.preventDefault();
      this.stepQuantity(parseInt(step.dataset.quantityStep, 10));
    });
  }

  stepQuantity(delta) {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + delta);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.button?.getAttribute('aria-busy') === 'true') return;

    this.setError('');
    this.setBusy(true);

    const formData = new FormData(this.form);
    // Ask for the cart drawer in the same response, so the cart is refreshed
    // from server-rendered Liquid rather than rebuilt client-side.
    formData.append('sections', 'cart-drawer');

    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || window.themeStrings?.addError);
      }

      document.dispatchEvent(
        new CustomEvent('theme:cart:refresh', {
          detail: { open: this.dataset.cartDrawer !== 'false', sections: data.sections },
        })
      );

      announce(window.themeStrings?.addedToCart || 'Added to cart');
    } catch (error) {
      this.setError(error.message || 'Something went wrong.');
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    if (!this.button) return;
    this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  setError(message) {
    if (!this.errorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = !message;
    if (message) announce(message);
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

export default ProductForm;
